// API Utility Functions

import type { ApiError, ApiResponse } from '@/types/redux';

/**
 * Handle API errors and extract error messages
 */
export const handleApiError = (error: any): string => {
  if (error?.data?.message) {
    return error.data.message;
  }
  
  if (error?.data?.errors) {
    const errorMessages = Object.values(error.data.errors).flat();
    return errorMessages.join(', ');
  }
  
  if (error?.error) {
    return error.error;
  }
  
  if (error?.status === 'FETCH_ERROR') {
    return 'Network error. Please check your connection.';
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Transform API response to a consistent format
 */
export const transformResponse = <T>(response: any): ApiResponse<T> => {
  if (response?.data) {
    return {
      success: true,
      data: response.data,
      message: response.message,
    };
  }
  
  return {
    success: true,
    data: response,
  };
};

/**
 * Build query string from params
 */
export const buildQueryString = (params: Record<string, any>): string => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((item) => queryParams.append(key, String(item)));
      } else {
        queryParams.append(key, String(value));
      }
    }
  });
  
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Format error for display
 */
export const formatError = (error: ApiError | any): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  return handleApiError(error);
};
